import type { AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import type { BranchId } from '../../config/constants';

export type ResolveLandingActionInput = {
  branchId: BranchId | null;
  isHydrated: boolean;
  isFirstStep: boolean;
  isBranchFirstStep: boolean;
  branchFromAnswers: AnswerValue;
  /** Destination of an in-flight intentional branch switch; null when none. */
  pendingBranchSwitch: BranchId | null;
  canReachCurrentStep: boolean;
  hasRedirected: boolean;
};

export type LandingAction =
  | { kind: 'none' }
  | { kind: 'reset-branch'; branchId: BranchId }
  | { kind: 'clear-redirect-guard' }
  | { kind: 'redirect-to-entry' };

/** Pure landing policy for `useHandleLanding`. */
export function resolveLandingAction({
  branchId,
  isHydrated,
  isFirstStep,
  isBranchFirstStep,
  branchFromAnswers,
  pendingBranchSwitch,
  canReachCurrentStep,
  hasRedirected,
}: ResolveLandingActionInput): LandingAction {
  if (!branchId || isFirstStep || !isHydrated) {
    return { kind: 'none' };
  }

  // Require a string primary_goal so `undefined !== branchId` cannot look like a mismatch.
  if (typeof branchFromAnswers === 'string' && branchFromAnswers !== branchId) {
    // Interrupt reset-branch: answers already moved; old URL may still be mounted.
    if (pendingBranchSwitch !== null && pendingBranchSwitch === branchFromAnswers) {
      return { kind: 'none' };
    }
    // Direct navigation onto another branch's first step (e.g. the entrypoint
    // banner jumping from one branch to another): override primary_goal instead
    // of wiping the whole session. Any other step keeps the guardrail below.
    if (isBranchFirstStep && !hasRedirected) {
      return { kind: 'reset-branch', branchId };
    }
    // Stale URL vs session (e.g. browser back after a branch switch).
    if (hasRedirected) {
      return { kind: 'none' };
    }
    return { kind: 'redirect-to-entry' };
  }

  // Seed deep-linked branch entry. Skip when redirect-to-entry just cleared answers
  // but the old branch URL is still mounted (would otherwise re-seed that branch).
  if (isBranchFirstStep && branchFromAnswers === undefined && !hasRedirected) {
    return { kind: 'reset-branch', branchId };
  }

  if (canReachCurrentStep) {
    return { kind: 'clear-redirect-guard' };
  }

  if (hasRedirected) {
    return { kind: 'none' };
  }

  return { kind: 'redirect-to-entry' };
}
